import { useEffect, useMemo, useState, type MouseEvent } from 'react';
import { format, isToday } from 'date-fns';
import { EyeOff, Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  type CourseFeedbackItem,
  useCreateCourseFeedbackMutation,
  useDeleteCourseFeedbackMutation,
  useGetCourseFeedbacksQuery,
  usePatchCourseFeedbackMutation,
} from '@/state/course';
import { useGetCurrentUserQuery } from '@/state/user';
import { skipToken } from '@reduxjs/toolkit/query';
import { cn } from '@/lib/utils';

interface CourseFeedbacksContentProps {
  tenant: string;
  courseId: number;
  canEdit: boolean;
  isLearner: boolean;
}

const MAX_RATING = 5;

const getInitials = (firstName?: string, lastName?: string) =>
  `${firstName?.[0] || ''}${lastName?.[0] || ''}`.trim() || 'U';

const formatFeedbackDate = (dateString: string) => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '-';
  if (isToday(date)) return `Today at ${format(date, 'h:mm a')}`;
  return format(date, 'MMM d, yyyy h:mm a');
};

const parseRatingValue = (rating: string | number | undefined) => {
  const value = Number(rating);
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(5, value));
};

const StarRow = ({ rating, size = 18 }: { rating: number; size?: number }) => (
  <div className="inline-flex items-center gap-0.5">
    {Array.from({ length: MAX_RATING }, (_, index) => {
      const position = index + 1;
      const fill = Math.max(0, Math.min(1, rating - (position - 1)));
      return (
        <span key={position} className="relative" style={{ width: size, height: size }}>
          <Star className="absolute left-0 top-0 text-muted-foreground/30" size={size} />
          <span className="absolute left-0 top-0 overflow-hidden" style={{ width: `${fill * 100}%` }}>
            <Star className="fill-amber-400 text-amber-400" size={size} />
          </span>
        </span>
      );
    })}
  </div>
);

const CourseFeedbacksContent = ({
  tenant,
  courseId,
  canEdit,
  isLearner,
}: CourseFeedbacksContentProps) => {
  const { data: currentUser } = useGetCurrentUserQuery(tenant ? { tenant } : skipToken);
  const { data: feedbacks = [], refetch } = useGetCourseFeedbacksQuery(
    tenant && courseId ? { tenant, courseId } : skipToken
  );
  const [createCourseFeedback, { isLoading: isCreating }] = useCreateCourseFeedbackMutation();
  const [patchCourseFeedback, { isLoading: isPatching }] = usePatchCourseFeedbackMutation();
  const [deleteCourseFeedback, { isLoading: isDeleting }] = useDeleteCourseFeedbackMutation();

  const [list, setList] = useState<CourseFeedbackItem[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFeedback, setEditingFeedback] = useState<CourseFeedbackItem | null>(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [hoverRating, setHoverRating] = useState<number | null>(null);
  const [feedbackAnonymous, setFeedbackAnonymous] = useState(true);
  const [feedbackToDelete, setFeedbackToDelete] = useState<CourseFeedbackItem | null>(null);

  useEffect(() => {
    setList(feedbacks);
  }, [feedbacks]);

  const averageRating = useMemo(() => {
    if (list.length === 0) return 0;
    const total = list.reduce((sum, item) => sum + parseRatingValue(item.rating), 0);
    return total / list.length;
  }, [list]);
  const visibleFeedbacks = useMemo(() => {
    if (!isLearner) return list;
    return list.filter((item) => {
      const isOwner = item.user?.id === currentUser?.id;
      if (item.hidden && !isOwner) return false;
      return true;
    });
  }, [list, isLearner, currentUser?.id]);

  const openCreate = () => {
    setEditingFeedback(null);
    setFeedbackText('');
    setFeedbackRating(0);
    setHoverRating(null);
    setFeedbackAnonymous(true);
    setDialogOpen(true);
  };

  const openEdit = (item: CourseFeedbackItem) => {
    setEditingFeedback(item);
    setFeedbackText(item.feedback || '');
    setFeedbackRating(parseRatingValue(item.rating));
    setHoverRating(null);
    setFeedbackAnonymous(item.anonymous);
    setDialogOpen(true);
  };

  const resolveRatingFromPointer = (index: number, event: MouseEvent<HTMLButtonElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    return clickX <= rect.width / 2 ? index - 0.5 : index;
  };

  const handleStarClick = (index: number, event: MouseEvent<HTMLButtonElement>) => {
    const nextRating = resolveRatingFromPointer(index, event);
    setFeedbackRating(nextRating);
  };

  const handleStarHover = (index: number, event: MouseEvent<HTMLButtonElement>) => {
    const nextRating = resolveRatingFromPointer(index, event);
    setHoverRating(nextRating);
  };

  const handleSubmit = async () => {
    const cleanFeedback = feedbackText.trim();
    if (!cleanFeedback) {
      toast.error('Feedback is required.');
      return;
    }
    if (feedbackRating <= 0) {
      toast.error('Please provide a rating.');
      return;
    }
    try {
      if (editingFeedback) {
        await patchCourseFeedback({
          tenant,
          feedbackId: editingFeedback.id,
          data: {
            feedback: cleanFeedback,
            rating: feedbackRating.toFixed(2),
            anonymous: feedbackAnonymous,
          },
        }).unwrap();
        toast.success('Feedback updated.');
      } else {
        await createCourseFeedback({
          tenant,
          data: {
            feedback: cleanFeedback,
            rating: Number(feedbackRating.toFixed(2)),
            anonymous: feedbackAnonymous,
            course: courseId,
          },
        }).unwrap();
        toast.success('Feedback submitted.');
      }
      setDialogOpen(false);
      setEditingFeedback(null);
      await refetch();
    } catch {
      toast.error('Failed to submit feedback.');
    }
  };

  const handleDelete = async () => {
    if (!feedbackToDelete) return;
    try {
      await deleteCourseFeedback({ tenant, feedbackId: feedbackToDelete.id }).unwrap();
      toast.success('Feedback deleted.');
      setFeedbackToDelete(null);
      await refetch();
    } catch {
      toast.error('Failed to delete feedback.');
    }
  };

  const handleToggleHidden = async (item: CourseFeedbackItem, nextHidden: boolean) => {
    const previous = list;
    setList((prev) =>
      prev.map((entry) =>
        entry.id === item.id
          ? {
              ...entry,
              hidden: nextHidden,
            }
          : entry
      )
    );
    try {
      await patchCourseFeedback({
        tenant,
        feedbackId: item.id,
        data: { hidden: nextHidden },
      }).unwrap();
    } catch {
      setList(previous);
      toast.error('Failed to update hidden status.');
    }
  };

  return (
    <div className="space-y-3 rounded-xl border bg-card p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b pb-3">
        <div className="inline-flex items-center gap-3">
          <p className="text-2xl font-semibold">Feedbacks</p>
          <div className="inline-flex items-center gap-1 text-base text-muted-foreground">
            <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
            <span className="font-semibold text-foreground">{averageRating.toFixed(1)}</span>
            <span>({list.length})</span>
          </div>
        </div>
        {isLearner ? (
          <Button type="button" variant="ghost" className="text-primary" onClick={openCreate}>
            <Plus className="h-4 w-4" />
            Give feedback
          </Button>
        ) : null}
      </div>

      {visibleFeedbacks.length === 0 ? (
        <div className="rounded-xl border border-dashed p-8 text-center text-muted-foreground">
          No feedbacks yet.
        </div>
      ) : (
        <div className="space-y-3">
          {visibleFeedbacks.map((item) => {
            const feedbackUser = item.user;
            const isOwner = feedbackUser?.id === currentUser?.id;
            const showAnonymous = item.anonymous && !isOwner;
            const showHiddenText = item.hidden && !isOwner && !canEdit;
            const hiddenDullClass = item.hidden && !isOwner ? 'opacity-60' : '';
            const displayName = showAnonymous
              ? 'Anonymous user'
              : feedbackUser
                ? `${feedbackUser.first_name} ${feedbackUser.last_name}`
                : 'Unknown user';
            return (
              <article key={item.id} className={`rounded-xl border bg-background p-4 ${hiddenDullClass}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarImage
                        src={showAnonymous ? undefined : feedbackUser?.photo_url || undefined}
                      />
                      <AvatarFallback>
                        {showAnonymous
                          ? 'A'
                          : getInitials(feedbackUser?.first_name, feedbackUser?.last_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-lg font-semibold">{displayName}</p>
                        {item.anonymous ? (
                          <span className="text-xs text-muted-foreground">( Sent as anonymous )</span>
                        ) : null}
                        {item.hidden ? (
                          <span className="inline-flex items-center gap-1 rounded-md border border-muted-foreground/30 px-2 py-0.5 text-xs text-muted-foreground">
                            <EyeOff className="h-3 w-3" />
                            Hidden
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                        <StarRow rating={parseRatingValue(item.rating)} />
                        <span>{formatFeedbackDate(item.created_date)}</span>
                      </div>
                    </div>
                  </div>

                  {isLearner && isOwner ? (
                    <div className="inline-flex items-center gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => openEdit(item)}
                        className="text-muted-foreground"
                      >
                        <Pencil className="h-4 w-4" />
                        Edit
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setFeedbackToDelete(item)}
                        className="text-muted-foreground"
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </Button>
                    </div>
                  ) : null}

                  {canEdit ? (
                    <div className="inline-flex items-center gap-2">
                      <button
                        type="button"
                        disabled={isPatching}
                        onClick={() => {
                          void handleToggleHidden(item, !item.hidden);
                        }}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          item.hidden ? 'bg-primary/70' : 'bg-muted'
                        }`}
                      >
                        <span
                          className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                            item.hidden ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </button>
                      <span className="text-sm text-muted-foreground">Hide</span>
                    </div>
                  ) : null}
                </div>

                <p className="mt-4 text-xl text-foreground">
                  {showHiddenText ? 'This feedback is hidden by instructor.' : item.feedback}
                </p>
              </article>
            );
          })}
        </div>
      )}

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setEditingFeedback(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-[640px]">
          <DialogHeader>
            <DialogTitle>{editingFeedback ? 'Edit feedback' : 'Add feedback'}</DialogTitle>
            <DialogDescription className="sr-only">Course feedback form.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-2xl font-medium">How was your experience?</p>
              <div className="mt-3 flex items-center gap-1">
                {Array.from({ length: MAX_RATING }, (_, index) => {
                  const position = index + 1;
                  const displayRating = hoverRating ?? feedbackRating;
                  const fill = Math.max(0, Math.min(1, displayRating - (position - 1)));
                  return (
                    <button
                      key={position}
                      type="button"
                      className="relative h-7 w-7"
                      onClick={(event) => handleStarClick(position, event)}
                      onMouseMove={(event) => handleStarHover(position, event)}
                      onMouseLeave={() => setHoverRating(null)}
                      aria-label={`Rate ${position} stars`}
                    >
                      <Star className="h-7 w-7 text-muted-foreground/30" />
                      <span
                        className="absolute inset-y-0 left-0 overflow-hidden"
                        style={{ width: `${fill * 100}%` }}
                      >
                        <Star className="h-7 w-7 fill-amber-400 text-amber-400" />
                      </span>
                    </button>
                  );
                })}
                <span className="ml-2 text-sm text-muted-foreground">
                  {(hoverRating ?? feedbackRating).toFixed(1)}
                </span>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Add feedback</label>
              <Textarea
                value={feedbackText}
                onChange={(event) => setFeedbackText(event.target.value)}
                placeholder="Add feedback"
                className="min-h-[120px] resize-none"
              />
            </div>
            <div className="flex items-center justify-between px-1 py-1">
              <p className="text-sm font-medium">Send anonymously</p>
              <button
                type="button"
                role="switch"
                aria-checked={feedbackAnonymous}
                onClick={() => setFeedbackAnonymous((prev) => !prev)}
                className={cn(
                  'relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors',
                  feedbackAnonymous ? 'bg-primary' : 'bg-muted-foreground/30'
                )}
              >
                <span
                  className={cn(
                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow-sm ring-0 transition-transform',
                    feedbackAnonymous ? 'translate-x-5' : 'translate-x-0.5'
                  )}
                />
              </button>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" onClick={() => void handleSubmit()} disabled={isCreating || isPatching}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={Boolean(feedbackToDelete)}
        onOpenChange={(open) => {
          if (!open) setFeedbackToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete feedback?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeleting}
              onClick={(event) => {
                event.preventDefault();
                void handleDelete();
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CourseFeedbacksContent;
